import { forwardRef, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import { Button, Drawer, IconButton } from '@c2fo/liquidity';
import { AngleRightIcon, ArrowLeftIcon, BellOnDuotoneIcon, StarIcon } from '@c2fo/liquidity/icons';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import NoDataState from '@/enterprise/components/NoDataState';
import useRecentAwards, { RECENT_AWARDS_DAYS, RecentClear } from '@/enterprise/data/useRecentAwards';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import ClearStatusChip from '@/enterprise/features/history/ClearStatusChip';
import { useReporting } from '@/reporting';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import { trimDateTimestamp } from '@/utils/trimDateTimestamp';
import useLocaleFormat from '@/utils/useLocaleFormat';
const AwardLink = forwardRef<HTMLAnchorElement, { clear: RecentClear; onSelect?: () => void }>(
  ({ clear, onSelect }, ref) => {
    const { t } = useTranslation();
    const { trackEnterpriseEvent } = useReporting();
    const { asRelativeDate, asCurrency } = useLocaleFormat();
    const { data: takerMarket } = useTakerMarkets(
      useCallback(
        (takerMarkets: TakerMarket[]) => {
          return takerMarkets.find(
            (tm) =>
              tm.takerDivisionUuid === clear.supplierMarket.supplierDivisionUuid &&
              tm.marketUuid === clear.supplierMarket.marketUuid
          );
        },
        [clear.supplierMarket.marketUuid, clear.supplierMarket.supplierDivisionUuid]
      )
    );
    const makerOrganizationUuid = clear.supplierMarket.makerOrganizationUuid;
    const marketUuid = clear.supplierMarket.marketUuid;
    const takerDivisionId = takerMarket?.takerDivisionId;
    const startDate = trimDateTimestamp(clear.depositDate);
    const endDate = trimDateTimestamp(clear.depositDate);

    const link = asEnterpriseLink(
      `customers/${makerOrganizationUuid}/markets/${marketUuid}/division/${takerDivisionId}/history?startDate=${startDate}&endDate=${endDate}`
    );

    return (
      <div className="group w-full px-4">
        <Link
          ref={ref}
          className="flex w-full items-center justify-between gap-4 border-b py-4 font-normal text-text-primary"
          onClick={() => {
            onSelect?.();
            trackEnterpriseEvent('recent-award-nav::clicked');
          }}
          to={link}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <StarIcon className="size-5 shrink-0 fill-text-secondary" />
              <div className="break-words text-xs text-text-secondary">{t('core.earlyPaymentAccepted')}</div>
            </div>
            <div className="pl-6">
              {takerMarket && (
                <>
                  <div className="break-words pt-1 text-sm">{takerMarket.makerOrganizationName}</div>
                  <div className="break-words text-sm text-text-secondary">{getTakerMarketName(takerMarket)}</div>
                  <div className="break-words text-sm text-text-secondary">
                    {getTakerMarketDivisionTitle(takerMarket)?.content}
                  </div>
                </>
              )}
              <div className="break-words font-bold">{asCurrency(clear.clearedInvoiceAmount, clear.currency)}</div>
              <div className="pt-1">
                <ClearStatusChip
                  isPendingClear={clear.isPendingClear}
                  isInstantPay={clear.isInstantPay}
                  withTooltip={false}
                />
              </div>
              <div className="break-words pt-2 text-xs text-text-secondary">{asRelativeDate(clear.clearTime)}</div>
            </div>
          </div>
          <div className="mr-1 flex size-[2.25rem] shrink-0 items-center justify-center rounded-full transition-colors duration-200 group-hover:bg-secondary-100">
            <AngleRightIcon className="size-6 fill-secondary-500" />
          </div>
        </Link>
      </div>
    );
  }
);
AwardLink.displayName = 'AwardLink';

const RecentAwardsList = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { data: awards } = useRecentAwards();
  const { asNumber } = useLocaleFormat();

  if (!awards?.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <NoDataState
          className="py-8"
          icon={<img src={emptystatements} alt="emptystatements" className="h-28 w-28" />}
          title={t('recentAwardsDrawer.noDataTitle')}
          message={
            <Trans
              i18nKey="recentAwardsDrawer.noDataWithin30DaysDescription"
              components={{
                consolidatedHistoryLink: (
                  <Link to={asEnterpriseLink('history')} className="font-medium text-blue-500" onClick={onClose} />
                ),
              }}
              values={{
                formattedDays: asNumber({ decimals: 0, value: RECENT_AWARDS_DAYS }),
              }}
            />
          }
        />
      </div>
    );
  }

  return awards?.map((clear) => <AwardLink key={clear.id} clear={clear} onSelect={onClose} />);
};

interface RecentAwardsDrawerProps {
  variant: 'desktop' | 'mobile';
}

const RecentAwardsDrawer = ({ variant }: RecentAwardsDrawerProps) => {
  const { t } = useTranslation();
  const isMobile = useMedia('(max-width: 768px)');
  const [isRecentAwardsDrawerOpen, setIsRecentAwardsDrawerOpen] = useState(false);

  const closeDrawer = () => setIsRecentAwardsDrawerOpen(false);
  const openDrawer = () => setIsRecentAwardsDrawerOpen(true);

  return (
    <>
      {variant === 'desktop' ? (
        <Button
          className="[&_span:first-of-type>svg]:size-5 [&_span:first-of-type]:size-5"
          icon={<BellOnDuotoneIcon className="fill-secondary-500" />}
          onClick={openDrawer}
          size="sm"
          variant="ancillary"
          {...(!isMobile && { 'data-intercom-target': 'recentAwards' satisfies IntercomDataTargetNames })}
        >
          {t('taker.recentAwardsDropdown.title')}
        </Button>
      ) : (
        <IconButton
          icon={BellOnDuotoneIcon}
          name={t('taker.recentAwardsDropdown.title')}
          onClick={openDrawer}
          variant="ancillary"
        />
      )}
      <Drawer
        backdropClassName="z-10"
        dialogClassName="z-10 w-full max-w-80 md:max-w-96"
        hideCloseButton
        isOpen={isRecentAwardsDrawerOpen}
        onClose={closeDrawer}
        title={
          <div className="-ml-1 flex items-center gap-2">
            <IconButton
              className="size-8 [&>svg]:size-5"
              icon={ArrowLeftIcon}
              name="close"
              onClick={closeDrawer}
              size="sm"
              variant="ancillary"
            />
            <p className="text-lg font-bold">{t('taker.recentAwardsDropdown.title')}</p>
          </div>
        }
        titleClassName="border-b"
      >
        <QueryBoundaries>
          <RecentAwardsList onClose={closeDrawer} />
        </QueryBoundaries>
      </Drawer>
    </>
  );
};

export default RecentAwardsDrawer;
